/**
 * React context for analytics.
 */

import React, { createContext, useState, type ReactNode } from 'react';
import type { AnalyticsEvent } from './types/events.js';
import { track as trackEvent } from './track.js';
import { getProviders } from './providers/index.js';
import { useMount } from '@studio/react-utils/hooks';

/**
 * Analytics context type.
 */
interface AnalyticsContextType {
  /**
   * Track an analytics event.
   */
  track: (event: AnalyticsEvent) => Promise<void>;

  /**
   * Identify a user.
   */
  identify: (userId: string, traits?: Record<string, any>) => Promise<void>;

  /**
   * Reset the analytics state.
   */
  reset: () => Promise<void>;

  /**
   * Whether analytics is initialized.
   */
  isInitialized: boolean;
}

/**
 * Default context value.
 */
const defaultContext: AnalyticsContextType = {
  track: async () => {},
  identify: async () => {},
  reset: async () => {},
  isInitialized: false
};

/**
 * React context for analytics.
 */
export const AnalyticsContext = createContext<AnalyticsContextType>(defaultContext);

/**
 * Props for the AnalyticsProvider component.
 */
interface AnalyticsProviderProps {
  children: ReactNode;
}

/**
 * Analytics provider component.
 *
 * This component initializes analytics providers and provides the analytics context.
 */
export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize providers
  useMount(() => {
    const initializeProviders = async () => {
      const providers = getProviders();
      await Promise.allSettled(providers.map((provider) => provider.initialize()));
      setIsInitialized(true);
    };

    initializeProviders();
  });

  /**
   * Identify a user with all providers.
   */
  const identify = async (userId: string, traits?: Record<string, any>) => {
    const providers = getProviders();
    await Promise.allSettled(providers.map((provider) => provider.identify(userId, traits)));
  };

  /**
   * Reset all providers.
   */
  const reset = async () => {
    const providers = getProviders();
    await Promise.allSettled(providers.map((provider) => provider.reset()));
  };

  return (
    <AnalyticsContext.Provider
      value={{
        track: trackEvent,
        identify,
        reset,
        isInitialized
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};
